import React, { useEffect, useRef } from "react";
import { useFieldMappings } from "../exports/hooks/useFieldMappings";
import { MappedFieldRow } from "../exports/components/MappedFieldRow";
import { AddCustomFieldButton } from "../exports/components/AddCustomFieldButton";
import { Checkbox } from "../../components/Checkbox";

// Canonical column catalog for template creation — support picks from a
// fixed, known-good list instead of hand-typing column names from memory.
// REPORT_COLUMNS mirrors public.get_conversions_list_hybrid()'s return shape
// (supabase-migrations/044_hybrid_timestamp_rpcs.sql) — the same columns
// rendered in the Reports tab. SYSTEM_COLUMNS are Audiohook's own internal
// IDs (assisted_conversions.audiohook_event_id / audiohook_impression_id) —
// not shown in Reports, but required for real platform mappings (e.g. CJ's
// orderId sources from audiohook_event_id, not the customer-side event_id),
// and audiohook_event_id is what the worker uses as the ledger's join key.
const REPORT_COLUMNS = [
  "event_id", "event_type", "event_value", "spend", "created_at",
  "event_timestamp", "country", "region", "city", "visitor_id",
];
const SYSTEM_COLUMNS = ["audiohook_event_id", "audiohook_impression_id"];
// Hidden from the advertiser-facing mapping UI (2026-08-27 request) — pixel
// pipeline internals, raw IP, and billing/attribution bookkeeping that
// advertisers shouldn't be picking for a third-party CSV. Support can still
// map these when a real destination genuinely needs one (e.g. a platform
// wants a fraud-check IP field). created_at is ALSO hidden from advertisers
// but isn't duplicated here — it's already in REPORT_COLUMNS above, which
// this whole picker is support-only to begin with.
const INTERNAL_ONLY_COLUMNS = [
  "event_tracking_url", "event_request_method", "user_ip_address",
  "attribution_score_updated_at", "augmented_impressions",
  "augmented_visitor_sessions", "billing_evaluated_at",
];

const rowStyle = (checked) => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "8px 10px",
  borderRadius: 8,
  border: `1px solid ${checked ? "#F90091" : "#E2E2E2"}`,
  background: checked ? "#FFF5FB" : "#FAFAFA",
  color: checked ? "#F90091" : "#151515",
  fontSize: 12,
  cursor: "pointer",
  userSelect: "none",
});

function ColumnCheckbox({ name, checked, onToggle }) {
  return (
    <label style={rowStyle(checked)}>
      <Checkbox checked={checked} onChange={onToggle} />
      {name}
    </label>
  );
}

// Single consolidated builder for a template's field_mappings: checking a
// column adds a column-type mapping row (target key defaults to the column
// name, editable inline via the same MappedFieldRow used in the advertiser
// mapping modal); "+ Add custom field" adds a static/hardcoded key+value
// row. available_columns is DERIVED from the checked column rows on save —
// there's no separate "available but unmapped" state to track, since every
// checked column always gets a sensible default mapping the advertiser can
// override later in their own job.
//
// value: the template's persisted field_mappings array (column|static
// entries, same shape as affiliate_exports.export_jobs.field_mappings).
// onChange(fieldMappings) fires on every change with the persist-ready shape
// (no client-only rowId).
// onValidityChange(isValid) fires alongside onChange so the parent form can
// disable saving on an empty / empty-key / duplicate-key mapping set (the
// same rules the DB trigger enforces).
export function AvailableColumnsPicker({ value = [], onChange, onValidityChange }) {
  // useFieldMappings only seeds its state from `value` once (on mount) —
  // the parent must remount this component (e.g. via a `key` tied to the
  // template being edited) when switching between templates, the same way
  // ExportJobMappingModal is remounted per-job. See ExportTemplatesAdmin.jsx.
  const { mappings, addColumnMapping, addCustomField, removeMapping, updateMapping, errors, isValid, toPersistShape } =
    useFieldMappings(value);

  // Keep the latest callbacks in refs, updated in an effect — never during
  // render. React can discard a render, and a ref write from a discarded
  // render would leak into the committed [mappings] effect below.
  const onChangeRef = useRef(onChange);
  const onValidityChangeRef = useRef(onValidityChange);
  useEffect(() => {
    onChangeRef.current = onChange;
    onValidityChangeRef.current = onValidityChange;
  });
  useEffect(() => {
    onChangeRef.current?.(toPersistShape());
    onValidityChangeRef.current?.(isValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mappings]);

  const columnRowFor = (name) => mappings.find((m) => m.type === "column" && m.source_column === name);

  const toggleColumn = (name) => {
    const existing = columnRowFor(name);
    if (existing) removeMapping(existing.rowId);
    else addColumnMapping(name);
  };

  const staticRows = mappings.filter((m) => m.type === "static");

  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, color: "#7b7b7b", marginBottom: 6 }}>
        Available Columns &amp; Default Mapping
      </div>

      <div style={{ fontSize: 10, color: "#7b7b7b", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
        Reporting Columns
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 6, marginBottom: 12 }}>
        {REPORT_COLUMNS.map((name) => (
          <ColumnCheckbox key={name} name={name} checked={!!columnRowFor(name)} onToggle={() => toggleColumn(name)} />
        ))}
      </div>

      <div style={{ fontSize: 10, color: "#7b7b7b", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>
        Internal System Fields
      </div>
      <div style={{ fontSize: 11, color: "#B8B8B8", marginBottom: 6 }}>
        Not shown in Reports — Audiohook's own event/impression IDs, needed for platform mappings that require a stable order ID (e.g. CJ's orderId).
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 6, marginBottom: 16 }}>
        {SYSTEM_COLUMNS.map((name) => (
          <ColumnCheckbox key={name} name={name} checked={!!columnRowFor(name)} onToggle={() => toggleColumn(name)} />
        ))}
      </div>

      <div style={{ fontSize: 10, color: "#7b7b7b", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>
        Internal-Only Fields
      </div>
      <div style={{ fontSize: 11, color: "#B8B8B8", marginBottom: 6 }}>
        Hidden from the advertiser-facing mapping UI (raw IP, pixel pipeline internals, billing/attribution bookkeeping) — only support can map these into a template.
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 6, marginBottom: 16 }}>
        {INTERNAL_ONLY_COLUMNS.map((name) => (
          <ColumnCheckbox key={name} name={name} checked={!!columnRowFor(name)} onToggle={() => toggleColumn(name)} />
        ))}
      </div>

      {mappings.filter((m) => m.type === "column").length > 0 && (
        <>
          <div style={{ fontSize: 10, color: "#7b7b7b", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
            Suggested target keys
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 8 }}>
            {mappings.filter((m) => m.type === "column").map((m) => (
              <MappedFieldRow key={m.rowId} mapping={m} error={errors[m.rowId]} onUpdate={updateMapping} onRemove={removeMapping} />
            ))}
          </div>
        </>
      )}

      {staticRows.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 8 }}>
          {staticRows.map((m) => (
            <MappedFieldRow key={m.rowId} mapping={m} error={errors[m.rowId]} onUpdate={updateMapping} onRemove={removeMapping} />
          ))}
        </div>
      )}
      <AddCustomFieldButton onClick={addCustomField} />
    </div>
  );
}
